// react
import { useEffect, useState } from 'react';

// types
import {
	Account,
	Comment,
	Pagination,
	Transaction,
	TransactionType,
	User,
} from '../../../../@types';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// mui
import {
	Container,
	Grid,
	Paper,
	TableCell,
	TableFooter as MuiTableFooter,
	TableRow,
	Typography,
	Tooltip,
} from '@mui/material';

// icons
import {
	ModeEditOutlined as IconModeEdit,
	RestoreOutlined as IconRestore,
} from '@mui/icons-material';

// ui
import { BtnMenu } from '../../../ui/BtnMenu/BtnMenu';
import { Flag } from '../../../ui/Flag/Flag';
import { Table, TableHeader } from '../../../ui/Table/Table';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { Time } from '../../../ui/Time/Time';
import { UserAvatar } from '../../../ui/UserAvatar/UserAvatar';

// links
import { LinkTransaction } from '../../../links/LinkTransaction/LinkTransaction';

// dialogs
import { DialogEditTransaction } from '../../../dialogs/DialogEditTransaction/DialogEditTransaction';

// utils
import { flagsByCurrency, formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';
import { defaultPagination } from '../../../../utils/defaults';
import { UserAvatarModified } from '../../../ui/UserAvatarModified/UserAvatarModified';

export const Activity = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { idAccount } = useParams();
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramOrder = searchParams.get('order') || 'DESC';
	const paramOrderBy = searchParams.get('orderBy') || 'date';
	const paramPage = Number(searchParams.get('page') || 1);

	// state
	const [account, setAccount] = useState<Account | null>(null);
	const [pagination, setPagination] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// fetch account
	useFetch({
		isEnabled: Boolean(idAccount),
		url: `${process.env.REACT_APP_API_URL}/accounts/${idAccount}?fields=currency,id`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setAccount(res.data[0]);
			}
		},
	});

	// fetch transactions
	const {
		fetchRequest: fetchTransactions,
		isLoading: isLoadingTransactions,
		refetch: refetchTransactions,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/transactions?account=${idAccount}&populate=activities,activities.actor,fromEntity,toEntity&page=${paramPage}&limit=${pagination.perPage}&orderBy=${paramOrderBy}&order=${paramOrder}&populate=comments.user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}

			if (res.pagination) {
				setPagination(res.pagination);
			}
		},
	});

	const transactionsValid = transactions.filter((t) => t.status !== 'REVERTED');
	const transactionsIn = transactionsValid.filter((t) => t.toAccount === Number(idAccount));
	const transactionsOut = transactionsValid.filter((t) => t.fromAccount === Number(idAccount));

	// define total in, prioritizing amountReceived
	const totalIn = transactionsIn.reduce((total, transaction) => {
		return total + ((transaction.amountReceived || transaction.amount) + transaction.tax);
	}, 0);

	// define total out
	const totalOut = transactionsOut.reduce((total, transaction) => {
		return total + transaction.amount + transaction.tax;
	}, 0);

	const headers: TableHeader[] = [
		{
			label: t('type'),
			render: (type: TransactionType, header: TableHeader, transaction: Transaction) => {
				return <LinkTransaction transaction={transaction} />;
			},
			value: 'type',
		},
		{
			label: t('recordedDate'),
			isSortable: true,
			render: (created: string, header, transaction: Transaction) => {
				return (
					<UserAvatarModified
						activityTypes={['TRANSACTION_CREATED']}
						date={transaction.created}
						transaction={transaction}
					/>
				);
			},
			sx: {
				minWidth: 160,
			},
			value: 'created',
		},
		{
			label: t('transactionDate'),
			isSortable: true,
			render: (date: string) => {
				return <Time date={date} />;
			},
			sx: {
				minWidth: 160,
			},
			value: 'date',
		},
		{
			label: t('inflow'),
			render: (amount: number, header: TableHeader, transaction: Transaction) => {
				const isIn = transaction.toAccount === Number(idAccount);

				let content = '-';

				if (isIn) {
					content = formatCurrency({
						currency: transaction.currencyReceived || transaction.currency,
						value: (transaction.amountReceived || amount) + transaction.tax,
					});
				}

				return (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>
							<Typography>{content}</Typography>
						</Grid>
						{isIn && transaction.status === 'REVERTED' && (
							<Grid item>
								<Tooltip arrow={true} placement="top" title={t('reverted')}>
									<IconRestore color="error" />
								</Tooltip>
							</Grid>
						)}
					</Grid>
				);
			},
			value: 'amount',
		},
		{
			label: t('outflow'),
			render: (total: number, header: TableHeader, transaction: Transaction) => {
				const isOut = transaction.fromAccount === Number(idAccount);

				let content = '-';

				if (isOut) {
					content = formatCurrency({ currency: transaction.currency, value: total });
				}

				return (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>
							<Typography>{content}</Typography>
						</Grid>
						{isOut && transaction.status === 'REVERTED' && (
							<Grid item>
								<Tooltip arrow={true} placement="top" title={t('reverted')}>
									<IconRestore color="error" />
								</Tooltip>
							</Grid>
						)}
					</Grid>
				);
			},
			value: 'total',
		},
		{
			label: t('latestComment'),
			render: (comments: Comment[], header: TableHeader, transaction: Transaction) => {
				return comments.length > 0 ? (
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography>{comments[0].comment}</Typography>
						</Grid>

						<Grid item xs={12}>
							<Grid alignItems="center" container spacing={2}>
								<Grid item>
									<UserAvatar user={comments[0]?.user as User} />
								</Grid>
								<Grid item>
									<Typography color="brand.grey.500" variant="caption">
										{formatDate({ date: new Date(comments[0].created) })}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				) : (
					<Typography>-</Typography>
				);
			},
			sx: {
				minWidth: 200,
			},
			value: 'comments',
		},
		{
			label: null,
			render: (value: unknown, header: TableHeader, transaction: Transaction) => {
				let children = t('editTransaction');

				if (transaction.type === 'ADVANCE') {
					children = t('editAdvance');
				} else if (transaction.type === 'CASH_BENEFIT') {
					children = t('editCashBenefit');
				} else if (transaction.type === 'DIRECT') {
					children = t('editDirectPayment');
				} else if (transaction.type === 'INITIAL') {
					children = t('editInitialDeposit');
				} else if (transaction.type === 'OPEX') {
					children = t('editOpEx');
				} else if (transaction.type === 'PAYMENT') {
					children = t('editPayment');
				} else if (transaction.type === 'REIMBURSEMENT') {
					children = t('editReimbursement');
				} else if (transaction.type === 'REPLENISHMENT') {
					children = t('editReplenishment');
				}

				if (
					[
						'ADVANCE',
						'CASH_BENEFIT',
						'DIRECT',
						'INITIAL',
						'OPEX',
						'PAYMENT',
						'REIMBURSEMENT',
						'REPLENISHMENT',
					].includes(transaction.type)
				) {
					return (
						<BtnMenu
							items={[
								{
									children,
									icon: <IconModeEdit />,
									onClick: () => {
										searchParams.set('dialog', 'editTransaction');
										searchParams.set('dialogTransaction', String(transaction.id));
										setSearchParams(searchParams);
									},
								},
							]}
						/>
					);
				}
			},
			value: 'actions',
		},
	];

	// fetch transactions when params change
	useEffect(() => {
		fetchTransactions();
	}, [fetchTransactions, paramOrder, paramOrderBy, paramPage]);

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Paper>
						<Table
							borderRadius={2}
							data={transactions}
							headers={headers}
							hasStripes={true}
							isLoading={isLoadingTransactions}
							sxTd={{
								textWrap: {
									xs: 'wrap',
									md: 'wrap',
								},
							}}>
							<MuiTableFooter>
								<TableRow key={`total`}>
									<TableCell></TableCell>
									<TableCell></TableCell>
									<TableCell component="th" sx={{ fontWeight: 600 }}>
										<Grid alignItems="center" container spacing={1}>
											{account?.currency && (
												<Grid item>
													<Flag code={flagsByCurrency[account?.currency]} />
												</Grid>
											)}
											<Grid item>{t('total')}</Grid>
										</Grid>
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{formatCurrency({ currency: account?.currency, value: totalIn })}
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										{formatCurrency({ currency: account?.currency, value: totalOut })}
									</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</MuiTableFooter>
						</Table>
					</Paper>
				</Grid>
				<Grid item xs={12}>
					<TableFooter numShowing={transactions.length} pagination={pagination} />
				</Grid>
			</Grid>
			<DialogEditTransaction
				isOpen={searchParams.get('dialog') === 'editTransaction'}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogTransaction');
					setSearchParams(searchParams);
				}}
				onSuccess={refetchTransactions}
			/>
		</Container>
	);
};
